import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { ViewProgressSummaryCardChart } from "components/molecules/ViewProgressSummaryCardChart";
import { Modal } from "components/molecules/Modal";
import { TestsDetailsTable } from "components/molecules/Table/TestsDetails/TestsDetailsTable";
import { Typography } from "components/atoms/Typography";
import { useScreenSize } from "hooks";
import { MOBILE_WIDTH } from "utils/constants";
import { useGetOverallProgressQuery } from "services/homeApi";
import { CloseIcon, Spinner } from "components/atoms/Icon";
import { FetchError } from "components/molecules";
import { DiagnosticExamFilterButtonSection } from "../DiagnosticExamFilterButtonSection";
import { GuaranteeTopicFocusFilterButtonSection } from "../GuaranteeTopicFocusFilterButtonSection";
import { TopicFocusTable } from "components/molecules/Table/TopicFocus/TopicFocusTable";
import { decodeToken } from "utils/functions/decodeToken";
import { getOfficeHoursDisplayName } from "utils/functions/getOfficeHoursDisplayName";
import { checkPastOrFutureDate } from "utils/functions/checkPastOrFutureDate";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import "./index.scss";

interface SelectedBtnState {
  [key: string]: boolean;
  "1": boolean;
  "2": boolean;
  "3": boolean;
  "4": boolean;
}

interface IProps {
  setIsShowMobileTestDetails?: Dispatch<SetStateAction<boolean>>;
  setIsShowMobileViewAllSummary?: Dispatch<SetStateAction<boolean>>;
  theme: "light" | "dark";
  setClickedGraphNameMobile?: Dispatch<SetStateAction<undefined | string>>;
}

export const ViewProgressSummaryCard: React.FC<IProps> = ({
  setIsShowMobileTestDetails,
  setIsShowMobileViewAllSummary,
  theme,
  setClickedGraphNameMobile,
}) => {
  const [toggleModal, setToggleModal] = useState(false);
  const screen = useScreenSize();
  const [selectedBtn, setSelectedBtn] = useState<SelectedBtnState>({
    "1": true,
    "2": false,
    "3": false,
    "4": false,
  });
  const [filterDropdown, setFilterDropdown] = useState<
    null | string | { [key: number]: string }
  >(null);
  const [clickedGraphName, setClickedGraphName] = useState<
    undefined | string
  >();
  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;
  const testType = payload ? payload.testtype : "";

  const { data, isLoading, isError, refetch, isFetching } =
    useGetOverallProgressQuery({
      Userid: userId,
      Productcode: productCode,
      Offeringcode: offeringCode,
    });

  const [triggerCourseIndexFetching, refetchCourseIndexResult] =
    useLazyGetCourseIndexQuery();

  const handleModalShow = (type?: string | undefined) => {
    setClickedGraphName(type);
    if (screen.width > MOBILE_WIDTH) {
      setToggleModal((prev) => !prev);
    } else {
      setIsShowMobileTestDetails && setIsShowMobileTestDetails((prev) => !prev);
      setClickedGraphNameMobile && setClickedGraphNameMobile(type);
    }
  };

  const getTitle = (type: string | undefined) => {
    switch (type) {
      case "Attendance":
        return "Attendance Details";
      case "Homework":
        return "Homework Details";
      case "Tests":
        return "Tests Details";
      case "TopicFocus":
        return `Guarantee Requirements: ${getOfficeHoursDisplayName(payload?.testtype || "")}`;
      default:
        return "Diagnostic Exams Details";
    }
  };

  const handleModalClose = () => {
    setToggleModal(false);
    setSelectedBtn({
      "1": true,
      "2": false,
      "3": false,
      "4": false,
    });
    setFilterDropdown(null);
    setIsScrolled(false);
    setClickedGraphName(undefined);
  };

  // view all, complete, incomplete, upcoming
  const getFilteterData = (dataArr: any, type: string): any => {
    if (
      type === "Diagnostics" &&
      filterDropdown &&
      Object.values(filterDropdown).length > 0
    ) {
      if (selectedBtn[1] === true) {
        return dataArr[Object.values(filterDropdown)[0]];
      } else if (selectedBtn[2] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status !== "Completed" &&
            value.Status !== "Complete" &&
            checkPastOrFutureDate("isFuture", value.DueDate)
        );
      } else if (selectedBtn[3] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status === "Complete" || value.Status === "Completed"
        );
      } else if (selectedBtn[4] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status !== "Complete" &&
            value.Status !== "Completed" &&
            checkPastOrFutureDate("isPast", value.DueDate)
        );
      }
    } else if (
      type === "Attendance" &&
      filterDropdown &&
      Object.values(filterDropdown).length > 0
    ) {
      if (selectedBtn[1] === true) {
        return dataArr[Object.values(filterDropdown)[0]];
      } else if (selectedBtn[2] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter((value: any) =>
          checkPastOrFutureDate("isFuture", value.ClassDateTZ)
        );
      } else if (selectedBtn[3] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) => value.Status === "Attended"
        );
      } else if (selectedBtn[4] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status !== "Attended" &&
            checkPastOrFutureDate("isPast", value.ClassDateTZ)
        );
      }
    } else if (
      type === "Homework" &&
      filterDropdown &&
      Object.values(filterDropdown).length > 0
    ) {
      if (selectedBtn[1] === true) {
        return dataArr[Object.values(filterDropdown)[0]];
      } else if (selectedBtn[2] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status !== "Completed" &&
            value.Status !== "Complete" &&
            checkPastOrFutureDate("isFuture", value.DueDate)
        );
      } else if (selectedBtn[3] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status === "Complete" || value.Status === "Completed"
        );
      } else if (selectedBtn[4] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status !== "Complete" &&
            value.Status !== "Completed" &&
            checkPastOrFutureDate("isPast", value.DueDate)
        );
      }
    } else if (type === "Tests") {
      if (selectedBtn[1] === true) {
        return dataArr;
      } else if (selectedBtn[2] === true) {
        return dataArr.filter(
          (value: any) =>
            value.Status !== "Completed" &&
            value.Status !== "Complete" &&
            checkPastOrFutureDate("isFuture", value.DueDate)
        );
      } else if (selectedBtn[3] === true) {
        return dataArr.filter(
          (value: any) =>
            value.Status === "Complete" || value.Status === "Completed"
        );
      } else if (selectedBtn[4] === true) {
        return dataArr.filter(
          (value: any) =>
            value.Status !== "Complete" &&
            value.Status !== "Completed" &&
            checkPastOrFutureDate("isPast", value.DueDate)
        );
      }
    } else if (
      type === "TopicFocus" &&
      filterDropdown &&
      Object.values(filterDropdown).length > 0
    ) {
      if (selectedBtn[1] === true) {
        return dataArr[Object.values(filterDropdown)[0]];
      } else if (selectedBtn[2] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            value.Status !== "Attended" &&
            checkPastOrFutureDate(
              "isFuture",
              value.NearFutureSegments.length > 0
                ? value.NearFutureSegments[0].StarttimeUTC
                : value.ClassDateTZ
            )
        );
      } else if (selectedBtn[3] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) => value.Status === "Attended"
        );
      } else if (selectedBtn[4] === true) {
        return dataArr[Object.values(filterDropdown)[0]].filter(
          (value: any) =>
            (value.Status === "Not Attended" && checkPastOrFutureDate(
              "isPast",
              value.NearFutureSegments.length > 0
                ? value.NearFutureSegments[0].StarttimeUTC
                : value.ClassDateTZ
            )) ||
            (value.Status !== "Attended" &&
              checkPastOrFutureDate(
                "isPast",
                value.NearFutureSegments.length > 0
                  ? value.NearFutureSegments[0].StarttimeUTC
                  : value.ClassDateTZ
              ))
        );
      }
    } else {
      const tempArr: any[] = [];
      // OLD State: view all, complete, incomplete, upcoming
      if (type === "Diagnostics") {
        for (const className in dataArr) {
          if (dataArr.hasOwnProperty(className)) {
            const classArray = dataArr[className];
            classArray.forEach((element: any) => {
              if (selectedBtn[1] === true) {
                tempArr.push(element);
              } else if (selectedBtn[2] === true) {
                if (
                  element.Status !== "Complete" &&
                  element.Status !== "Completed" &&
                  checkPastOrFutureDate("isFuture", element.DueDate)
                ) {
                  tempArr.push(element);
                }
              } else if (selectedBtn[3] === true) {
                if (
                  element.Status === "Complete" ||
                  element.Status === "Completed"
                ) {
                  tempArr.push(element);
                }
              } else if (selectedBtn[4] === true) {
                if (
                  element.Status !== "Complete" &&
                  element.Status !== "Completed" &&
                  checkPastOrFutureDate("isPast", element.DueDate)
                ) {
                  tempArr.push(element);
                }
              }
            });
          }
        }
        return tempArr;
      } else if (type === "Attendance") {
        for (const className in dataArr) {
          if (dataArr.hasOwnProperty(className)) {
            const classArray = dataArr[className];
            classArray.forEach((element: any) => {
              if (selectedBtn[1] === true) {
                tempArr.push(element);
              } else if (selectedBtn[2] === true) {
                if (checkPastOrFutureDate("isFuture", element.ClassDateTZ)) {
                  tempArr.push(element);
                }
              } else if (selectedBtn[3] === true) {
                if (element.Status === "Attended") {
                  tempArr.push(element);
                }
              } else if (selectedBtn[4] === true) {
                if (
                  element.Status !== "Attended" &&
                  checkPastOrFutureDate("isPast", element.ClassDateTZ)
                ) {
                  tempArr.push(element);
                }
              }
            });
          }
        }
        return tempArr;
      } else if (type === "Homework") {
        for (const className in dataArr) {
          if (dataArr.hasOwnProperty(className)) {
            const classArray = dataArr[className];
            classArray.forEach((element: any) => {
              if (selectedBtn[1] === true) {
                tempArr.push(element);
              } else if (selectedBtn[2] === true) {
                if (
                  element.Status !== "Complete" &&
                  element.Status !== "Completed" &&
                  checkPastOrFutureDate("isFuture", element.DueDate)
                ) {
                  tempArr.push(element);
                }
              } else if (selectedBtn[3] === true) {
                if (
                  element.Status === "Complete" ||
                  element.Status === "Completed"
                ) {
                  tempArr.push(element);
                }
              } else if (selectedBtn[4] === true) {
                if (
                  element.Status !== "Complete" &&
                  element.Status !== "Completed" &&
                  checkPastOrFutureDate("isPast", element.DueDate)
                ) {
                  tempArr.push(element);
                }
              }
            });
          }
        }

        return tempArr;
      } else if (type === "TopicFocus") {
        for (const className in dataArr) {
          if (dataArr.hasOwnProperty(className)) {
            const classArray = dataArr[className];
            classArray.forEach((element: any) => {
              if (selectedBtn[1] === true) {
                tempArr.push(element);
              } else if (selectedBtn[2] === true) {
                if (
                  element.Status !== "Attended" &&
                  checkPastOrFutureDate(
                    "isFuture",
                    element.NearFutureSegments.length > 0
                      ? element.NearFutureSegments[0].StarttimeUTC
                      : element.ClassDateTZ
                  )
                ) {
                  tempArr.push(element);
                }
              } else if (selectedBtn[3] === true) {
                if (element.Status === "Attended") {
                  tempArr.push(element);
                }
              } else if (selectedBtn[4] === true) {
                if (
                  (element.Status === "Not Attended" && checkPastOrFutureDate(
                    "isPast",
                    element.NearFutureSegments.length > 0
                      ? element.NearFutureSegments[0].StarttimeUTC
                      : element.ClassDateTZ
                  )) ||
                  (element.Status !== "Attended" &&
                    checkPastOrFutureDate(
                      "isPast",
                      element.NearFutureSegments.length > 0
                        ? element.NearFutureSegments[0].StarttimeUTC
                        : element.ClassDateTZ
                    ))
                ) {
                  tempArr.push(element);
                }
              }
            });
          }
        }
        return tempArr;
      }

      return dataArr;
    }
  };

  useEffect(() => {
    if (toggleModal === true && screen.width < MOBILE_WIDTH) {
      handleModalClose();
    }
  }, [screen.width]);

  const [isScrolled, setIsScrolled] = useState(false);
  const bodyWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (bodyWrapperRef.current) {
        if (bodyWrapperRef.current.scrollTop > 0) {
          setIsScrolled(true);
        } else {
          setIsScrolled(false);
        }
      }
    };

    const wrapper = bodyWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener("scroll", handleScroll);
      }
    };
  }, [clickedGraphName]);

  return (
    <>
      <div className="view-progress-card-wrapper">
        <div className="view-progress-card-chart-wrapper">
          <ViewProgressSummaryCardChart
            onClick={handleModalShow}
            theme={theme}
            setIsShowMobileViewAllSummary={setIsShowMobileViewAllSummary}
            isShowViewAll={false}
            data={data}
            isLoading={isLoading}
            isError={isError}
            isFetching={isFetching}
          />
        </div>
      </div>
      <Modal
        isOpen={toggleModal}
        onClose={handleModalClose}
        style="view-progress-summary-modal"
        theme={theme}
      >
        {isLoading ? (
          <Spinner theme={theme} />
        ) : isError ? (
          <FetchError content="Oops! failed to load resources." />
        ) : (
          data && (
            <div className={classNames("view-progress-modal-body-wrapper")}>
              <div className={classNames("view-progress-modal-header-wrapper")}>
                <div className="title-wrapper">
                  <Typography
                    label={getTitle(clickedGraphName)}
                    variant="h4Bold"
                  />
                </div>
                <CloseIcon onClick={handleModalClose} theme={theme} />
              </div>
              {clickedGraphName === "Diagnostics" ? (
                <>
                  <DiagnosticExamFilterButtonSection
                    selectedBtn={selectedBtn}
                    setSelectedBtn={setSelectedBtn}
                    setDropDownSelection={setFilterDropdown}
                    selectedOption={filterDropdown}
                    placeholder="All Subjects"
                    btnsLabelOrder={[
                      "View All",
                      "Upcoming",
                      "Complete",
                      "Past Due",
                    ]}
                    dropdownData={Object.keys(data.Diagnostics).sort((a, b) =>
                      a.localeCompare(b)
                    )}
                  />
                  <div
                    ref={bodyWrapperRef}
                    className={classNames("view-progress-modal-table-wrapper")}
                  >
                    <TestsDetailsTable
                      type="Diagnostics"
                      data={getFilteterData(data.Diagnostics, "Diagnostics")}
                      isFetching={isFetching}
                      refetch={refetch}
                      theme={theme}
                      courseIndexIsFetching={refetchCourseIndexResult.isFetching}
                      courseIndexRefetch={triggerCourseIndexFetching}
                      isTableBodyScrolled={isScrolled}
                      onParentModalClose={handleModalClose}
                    />
                  </div>
                </>
              ) : null}

              {clickedGraphName === "Attendance" ? (
                <>
                  <DiagnosticExamFilterButtonSection
                    selectedBtn={selectedBtn}
                    setSelectedBtn={setSelectedBtn}
                    setDropDownSelection={setFilterDropdown}
                    selectedOption={filterDropdown}
                    placeholder="All Classes"
                    dropdownData={Object.keys(data.Attendance).sort((a, b) =>
                      a.localeCompare(b)
                    )}
                    btnsLabelOrder={[
                      "View All",
                      "Upcoming",
                      "Attended",
                      "Not Attended",
                    ]}
                    hideDropdown={testType === "ACT" || testType === "SAT"}
                  />
                  <div
                    ref={bodyWrapperRef}
                    className={classNames("view-progress-modal-table-wrapper")}
                  >
                    <TestsDetailsTable
                      type="attendance"
                      data={getFilteterData(data.Attendance, "Attendance")}
                      theme={theme}
                      isTableBodyScrolled={isScrolled}
                      onParentModalClose={handleModalClose}
                    />
                  </div>
                </>
              ) : null}

              {clickedGraphName === "Homework" ? (
                <>
                  <DiagnosticExamFilterButtonSection
                    selectedBtn={selectedBtn}
                    setSelectedBtn={setSelectedBtn}
                    setDropDownSelection={setFilterDropdown}
                    selectedOption={filterDropdown}
                    placeholder="All Classes"
                    btnsLabelOrder={[
                      "View All",
                      "Upcoming",
                      "Complete",
                      "Past Due",
                    ]}
                    dropdownData={Object.keys(data.Homework).sort((a, b) =>
                      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
                    )}
                  />
                  <div
                    ref={bodyWrapperRef}
                    className={classNames("view-progress-modal-table-wrapper")}
                  >
                    <TestsDetailsTable
                      type="homework"
                      data={getFilteterData(data.Homework, "Homework")}
                      isFetching={isFetching}
                      refetch={refetch}
                      theme={theme}
                      courseIndexIsFetching={refetchCourseIndexResult.isFetching}
                      courseIndexRefetch={triggerCourseIndexFetching}
                      isTableBodyScrolled={isScrolled}
                      onParentModalClose={handleModalClose}
                    />
                  </div>
                </>
              ) : null}

              {clickedGraphName === "Tests" ? (
                <>
                  <DiagnosticExamFilterButtonSection
                    selectedBtn={selectedBtn}
                    setSelectedBtn={setSelectedBtn}
                    hideDropdown={true}
                    btnsLabelOrder={[
                      "View All",
                      "Upcoming",
                      "Complete",
                      "Past Due",
                    ]}
                  />
                  <div
                    ref={bodyWrapperRef}
                    className={classNames("view-progress-modal-table-wrapper")}
                  >
                    <TestsDetailsTable
                      data={getFilteterData(data.Tests, "Tests")}
                      type="Tests"
                      isFetching={isFetching}
                      refetch={refetch}
                      theme={theme}
                      courseIndexIsFetching={refetchCourseIndexResult.isFetching}
                      courseIndexRefetch={triggerCourseIndexFetching}
                      isTableBodyScrolled={isScrolled}
                      onParentModalClose={handleModalClose}
                    />
                  </div>
                </>
              ) : null}

              {clickedGraphName === "TopicFocus" ? (
                <>
                  <GuaranteeTopicFocusFilterButtonSection
                    selectedBtn={selectedBtn}
                    setSelectedBtn={setSelectedBtn}
                    setDropDownSelection={setFilterDropdown}
                    selectedOption={filterDropdown}
                    placeholder="All Subjects"
                    btnsLabelOrder={[
                      "View All",
                      "Upcoming",
                      "Attended",
                      "Not Attended",
                    ]}
                    dropdownData={Object.keys(data.TopicFocus).sort((a, b) =>
                      a.localeCompare(b)
                    )}
                  />
                  <div
                    ref={bodyWrapperRef}
                    className={classNames("view-progress-modal-table-wrapper")}
                  >
                    <TopicFocusTable
                      type="topicFocus"
                      data={getFilteterData(data.TopicFocus, "TopicFocus")}
                      theme={theme}
                      isTableBodyScrolled={isScrolled}
                    />
                  </div>
                </>
              ) : null}
            </div>
          )
        )}
      </Modal>
    </>
  );
};
